import { FlexBox, Text } from '@dixa/lib-shared-habla';
import { useMemo } from 'react';

import { ConversationAuditLogEntry } from 'apis/auditLogs';
import { filesApi } from 'apis/files';
import { CodeHighlight } from 'components/codeHighlight';

import { LogEntry } from '../logEntry';
import styles from '../logEntry/logEntry.module.scss';

import { AutomationTaskExecutionActivity } from './automationExecutionActivity';
import { getToolCallText, ToolCallDetails } from './toolCallDetails';

type RoutedThroughChatbotToolCalledProps = {
  entry: ConversationAuditLogEntry<'CHATBOT_TOOL_CALLED'>;
};

export const RoutedThroughChatbotToolCalled = ({
  entry,
}: RoutedThroughChatbotToolCalledProps): JSX.Element | null => {
  const { tool_name, tool_type, input, output, execution_data, parent } = entry;

  const {
    content: outputContent,
    isLoading,
    isError,
  } = filesApi.useReadFileQuery(
    { path: output?.s3_path || '' },
    {
      skip: !output?.s3_path,
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        content: data?.data?.content,
      }),
    },
  );

  const outputResult = useMemo(() => {
    if (isError) {
      return (
        <FlexBox as="div" padding="0 300">
          <Text.Paragraph variant="3" subdued>
            Error loading output
          </Text.Paragraph>
        </FlexBox>
      );
    }
    if (isLoading || !outputContent) {
      return (
        <FlexBox as="div" padding="0 300">
          <Text.Paragraph variant="3" subdued>
            Loading output...
          </Text.Paragraph>
        </FlexBox>
      );
    }
    return (
      <CodeHighlight
        languages={['json']}
        highlightClassName={styles.codeHighlight}
        style={{
          padding: '100 100',
          overflow: 'scroll',
          maxHeight: '500px',
        }}
      >
        {typeof outputContent === 'string'
          ? outputContent
          : JSON.stringify(outputContent, null, 2)}
      </CodeHighlight>
    );
  }, [isError, isLoading, outputContent]);
  if (
    tool_type === 'STATIC' &&
    tool_name === 'think' &&
    output?.s3_path &&
    outputResult
  ) {
    return (
      <LogEntry
        entry={entry}
        icon="brain"
        text={`AI agent thinking`}
        isExpandable
      >
        <FlexBox margin="200 0 0 500" gap="300" flexDirection="column">
          <FlexBox gap="200" flexDirection="column">
            {outputResult}
          </FlexBox>
          {execution_data?.type === 'automation' &&
            execution_data.automation && (
              <AutomationTaskExecutionActivity
                executionData={execution_data.automation}
              ></AutomationTaskExecutionActivity>
            )}
        </FlexBox>
      </LogEntry>
    );
  }
  if (tool_type === 'STATIC') {
    return null;
  }

  const toolCallText = getToolCallText(tool_type, tool_name);
  const secondaryText = parent ? `Procedure: ${parent.tool_name}` : null;
  return (
    <LogEntry
      entry={entry}
      icon="wrench"
      text={toolCallText}
      secondaryText={secondaryText}
      isExpandable
    >
      <FlexBox margin="200 0 0 500" gap="300" flexDirection="column">
        <ToolCallDetails
          input={input}
          output={output}
          execution_data={execution_data}
        />
      </FlexBox>
    </LogEntry>
  );
};
